export const FONT = "Inter, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif";

export const STEP_TYPE_CONFIG = {
    ACTION: { icon: 'flash-outline', colorVariant: 'primary', label: 'Action' },
    CONDITION: { icon: 'git-branch-outline', colorVariant: 'warning', label: 'Condition' },
    DELAY: { icon: 'time-outline', colorVariant: 'info', label: 'Delay' },
    TASK: { icon: 'checkbox-outline', colorVariant: 'success', label: 'Task' },
    TRIGGER: { icon: 'play-outline', colorVariant: 'primary', label: 'Trigger' },
};

// Predefined TASK steps: fixed outreach types the assignee performs from their
// own device. When the assignee opens the spawned task occurrence, Reach opens
// their native SMS / dialer / email app with the enrollee's contact info
// prefilled. Icons are Ionicons names (Unicon default library).
export const PREDEFINED_TASK_TYPES = {
    PERSONAL_SMS: {
        label: 'Personal SMS',
        icon: 'chatbubble-ellipses-outline',
        configHint: 'Write the message the assignee will send. Opens their SMS app with the enrollee\u2019s number and this message prefilled.',
    },
    PERSONAL_PHONE_CALL: {
        label: 'Personal Phone Call',
        icon: 'call-outline',
        configHint: 'Add talking points for the assignee. Opens their phone app with the enrollee\u2019s number.',
    },
    PERSONAL_EMAIL: {
        label: 'Personal Email',
        icon: 'mail-outline',
        configHint: 'Add a subject and message. Opens the assignee\u2019s email app with the enrollee\u2019s address prefilled.',
    },
};

export const PREDEFINED_TASK_TYPE_OPTIONS = Object.entries(PREDEFINED_TASK_TYPES).map(([value, def]) => ({ value, label: def.label }));

export const ACTION_TYPE_LABELS = {
    SEND_EMAIL: 'Send Email',
    SEND_SMS: 'Send SMS',
    ENROLL_TRAINING: 'Enroll in Training',
    ADD_TO_SMALL_GROUP: 'Add to Small Group',
    POST_TO_BOARD: 'Post to Board',
    ADD_TO_ROLE: 'Add to Role',
    INVITE_TO_REACH: 'Invite to Reach',
    TRIGGER_PATH: 'Trigger Automation',
    UPDATE_PERSON: 'Update Person',
    REGISTER_FOR_EVENT: 'Register for Event',
    CHECK_INTO_EVENT: 'Check Into Event',
    RESOLVE_PERSON: 'Resolve Person',
    UPSERT_PERSON: 'Create or Update Person',
    CREATE_TASK: 'Create Task',
    WAIT_FOR_TASK: 'Wait for Task',
    START_FOLLOW_UP_PATH: 'Start Follow Up Path',
    INVOKE_AUTOMATION: 'Invoke Automation',
    REQUEST_APPROVAL: 'Request Approval',
};

export const ADVANCE_LABELS = {
    AUTOMATIC: 'Auto',
    MANUAL: 'Manual',
};

export const TRIGGER_LABELS = {
    MANUAL: 'Manual Run',
    PATH_ACTION: 'Automation Action',
    PERSON_CREATED: 'Person Created',
    FORM_ENTRY: 'Form Entry',
    TRAINING_COMPLETED: 'Training Module Completed',
    SMALLGROUP_COMPLETED: 'Small Group Completed (Member)',
    SCHEDULE: 'Scheduled',
    ATTENDANCE: 'Attendance Trend',
    EVENT_CHECK_IN: 'Event Check In',
    FOLLOW_UP_ATTENTION_ENTERED: 'Attention Entered',
    FOLLOW_UP_ENROLLMENT_STARTED: 'Follow Up Enrollment Started',
    FOLLOW_UP_ENROLLMENT_COMPLETED: 'Follow Up Enrollment Completed',
    FOLLOW_UP_ENROLLMENT_CANCELLED: 'Follow Up Enrollment Cancelled',
    FOLLOW_UP_CARE_STARTED: 'Care Started',
    FOLLOW_UP_CARE_CLOSED: 'Care Closed',
    FOLLOW_UP_TOUCH_RECORDED: 'Follow Up Touch Recorded',
    TASK_COMPLETED: 'Task Completed',
    TASK_CANCELLED: 'Task Cancelled',
};

export const TRIGGER_DESCRIPTIONS = {
    MANUAL: 'Manually start runs for people or roles in this automation. Use the Run buttons in the sidebar.',
    PATH_ACTION: 'Triggered automatically when another automation&rsquo;s action starts a run here.',
    PERSON_CREATED: 'Triggered automatically when a new person is created in the system.',
    FORM_ENTRY: 'Triggered when someone submits a connected form.',
    TRAINING_COMPLETED: 'Triggered when a person completes a training module.',
    SMALLGROUP_COMPLETED: 'Triggered when a person completes a small group as a member.',
    SCHEDULE: 'Triggered on a recurring schedule. Configure via Manage Schedule in the sidebar.',
    ATTENDANCE: 'Trigger based on a person&rsquo;s attendance trend &mdash; missed weeks or attendance milestones. Configure via Manage Attendance Trend in the sidebar.',
    EVENT_CHECK_IN: 'Triggered when someone checks in to a linked event. Link an event instance via its Check-in Automation setting.',
};

export const TRIGGER_OPTIONS = Object.entries(TRIGGER_LABELS).map(([value, label]) => ({ value, label }));

// ─── Action param descriptors (frontend display metadata only) ───────────────
// The backend path-variables edge function is the source of truth for which
// variables exist. This map describes each action's params locally so the UI
// knows how to filter variables (by type/semantic) and which params support
// text interpolation.
export const ACTION_PARAM_DEFS = {
    SEND_EMAIL:        { templateID: { type: 'uuid' }, recipientIDs: { type: 'uuid', semantic: 'personID', multiple: true } },
    SEND_SMS:          { message: { type: 'string', text: true }, recipientIDs: { type: 'uuid', semantic: 'personID', multiple: true } },
    ENROLL_TRAINING:   { trainingModuleID: { type: 'uuid', semantic: 'trainingModuleID' } },
    ADD_TO_SMALL_GROUP:{ smallGroupID: { type: 'uuid', semantic: 'groupID' } },
    POST_TO_BOARD:     { boardID: { type: 'uuid' }, title: { type: 'string', text: true }, content: { type: 'string', text: true }, postAsPersonID: { type: 'uuid', semantic: 'personID' } },
    ADD_TO_ROLE:       { roleID: { type: 'uuid', semantic: 'roleID' } },
    INVITE_TO_REACH:   {},
    TRIGGER_PATH:      { targetPathID: { type: 'uuid', semantic: 'pathID' } },
    UPDATE_PERSON:     { fields: { dynamic: true, valueType: 'string' } },
    REGISTER_FOR_EVENT:{ eventInstanceID: { type: 'uuid', semantic: 'eventInstanceID' } },
    CHECK_INTO_EVENT:   { eventInstanceID: { type: 'uuid', semantic: 'eventInstanceID' } },
};

// Updatable People columns for UPDATE_PERSON action.
// Verified against the public.People table schema (Supabase MCP) — only
// columns that exist on People AND are safe to write via a direct update.
// Note: phone is an ARRAY column on People but the app manages phones via
// PeoplePhones; excluded here to avoid confusion. maritalStatus and
// membershipStatus do NOT exist as People columns (they appear in condition
// labels but are custom-field-based, not direct columns).
// `type` mirrors the CustomField type system so the value input renderer
// can pick the correct input component (DatePickerInput, SelectInput, etc).
export const UPDATE_PERSON_FIELDS = [
    { value: 'firstName', label: 'First Name', type: 'Text' },
    { value: 'lastName', label: 'Last Name', type: 'Text' },
    { value: 'email', label: 'Email', type: 'Text' },
    { value: 'gender', label: 'Gender', type: 'Dropdown', options: ['Male', 'Female'] },
    { value: 'dateOfBirth', label: 'Date of Birth', type: 'Date' },
];

export const RULE_TYPE_LABELS = {
    ENROLLEE_FIELD: 'Field',
    PERSON_FIELD: 'Person Field',
    TIME_OF_DAY: 'Time of Day',
    SMALL_GROUP_STATUS: 'Small Group Status',
    TRAINING_STATUS: 'Training Status',
    REACH_USER_STATUS: 'Reach User Status',
};

export const FIELD_LABELS = {
    'person.age': 'Age',
    'person.maritalStatus': 'Marital Status',
    'person.membershipStatus': 'Membership Status',
    'person.gender': 'Gender',
};

export const OPERATOR_LABELS = {
    equals: 'equals',
    not_equals: 'does not equal',
    less_than: 'less than',
    greater_than: 'greater than',
    contains: 'contains',
    not_contains: 'does not contain',
};

export const STATUS_LABELS = {
    ACTIVE: 'Active',
    INACTIVE: 'Inactive',
    COMPLETED: 'Completed',
    PENDING: 'Pending',
    MEMBER: 'Member',
    VISITOR: 'Visitor',
    REGULAR: 'Regular',
};

export const TIME_OF_DAY_LABELS = {
    MORNING: 'Morning (6-12)',
    AFTERNOON: 'Afternoon (12-18)',
    EVENING: 'Evening (18-22)',
    NIGHT: 'Night (22-6)',
};

export const CONDITION_EDGE_COLOR = '#f59e0b';
export const DEFAULT_EDGE_COLOR = '#94a3b8';

export const NODE_WIDTH = 230;
export const TRIGGER_HEIGHT = 240;
export const STEP_HEIGHT = 180;
export const TASK_STEP_HEIGHT = 220;
export const LABEL_WIDTH = 75;
export const LABEL_HEIGHT = 32;
export const ADD_STEP_WIDTH = 108;
export const STUB_WIDTH = 108;
export const STUB_GAP = 16;
export const STUB_Y = 24;
export const TRIGGER_Y = 20;
export const STEP_BASE_Y = 300;
export const STEP_GAP_Y = 240;
// Vertical gap (node edge to node edge) used by the dagre auto-layout ("Tidy Up");
// also used when placing newly added steps so both stay consistent.
export const LAYOUT_GAP_Y = 110;
export const TRIGGER_STEP_GAP_Y = STEP_BASE_Y - TRIGGER_Y;
export const BRANCH_GAP_Y = 210;
export const LABEL_SLOT_GAP = 24;
